import { ActionTree } from 'vuex';
import { RootState, AuthState } from '@/types/state';
import { CredentialsInterface, UserDetailsInterface, AuthenticateResponse } from '@/types/auth';
import AuthService from '@/services/AuthService';
import LogService from '@/services/LogService';

const actions: ActionTree<AuthState, RootState> = {
  authenticate(context, credentials: CredentialsInterface): Promise<void> {
    return new Promise((resolve, reject) => {
      AuthService.login(credentials.username, credentials.password)
        .then((response: AuthenticateResponse) => {
          if (!response.authenticated) {
            reject();
            return;
          }

          context.commit('setAuthenticated', true);
        }).then(() => {
          AuthService.fetchFreshUserDetails()
            .then((details: UserDetailsInterface) => {
              context.commit('setUserDetails', details);
              context.dispatch('syncToService');
            });

          resolve();
        }).catch(reject);
    });
  },

  logout(context): Promise<void> {
    return AuthService.logout()
      .then(() => {
        context.dispatch('reset');
        return Promise.resolve();
      });
  },

  async loadStoredAuth(context): Promise<void> {
    if (! await AuthService.isAuthenticated()) {
      return;
    }

    const authDetails = AuthService.getStoredUser();

    context.commit('setAuthenticated', true);
    context.commit('setUserDetails', authDetails);
    context.dispatch('syncToService');

    AuthService.fetchFreshUserDetails()
      .then((details: UserDetailsInterface) => {
        context.commit('setUserDetails', details);
        context.dispatch('syncToService');
      })
      .catch(() => context.commit('reset'));
  },

  updateUserDetails(context, details: UserDetailsInterface): void {
    context.commit('setUserDetails', details);
    context.dispatch('syncToService');
  },

  syncToService(context): void {
    const { userDetails } = context.state;

    if (!userDetails) {
      LogService.warn('Tried to sync the auth from the store to the service, but some of the stored auth was empty.');
      return;
    }

    AuthService.storeUser(userDetails);
  },

  reset(context): void {
    context.commit('reset');
    AuthService.clearStoredUser();
  },

};

export default actions;
